﻿import { Plus } from "lucide-react";
import type { UseFormReturn } from "react-hook-form";;
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { CollabFormValues } from "@/hooks/team/useTeamData";
import {  formatPhone } from "@/hooks/team/useTeamData";

export function CollabFormModal({
  isOpen,
  setIsOpen,
  editingCollab,
  form,
  onSubmit,
  isPending,
}: {
  isOpen: boolean;
  setIsOpen: (val: boolean) => void;
  editingCollab: any;
  form: UseFormReturn<CollabFormValues>;
  onSubmit: (data: CollabFormValues) => void;
  isPending: boolean;
}) {
  const selectedRole = form.watch("system_role");

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button className="bg-blue-600 hover:bg-blue-700 font-bold gap-2 shadow-lg shadow-blue-900/20">
          <Plus size={18} /> Novo Colaborador
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[450px] bg-slate-900 border-slate-700 text-slate-100">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {editingCollab ? "Editar Funcionário" : "Cadastrar Funcionário"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 mt-2">
          <div className="grid gap-2">
            <Label className="text-slate-300">Nome Completo *</Label>
            <Input
              {...form.register("name")}
              className="bg-slate-800 border-slate-700"
              placeholder="Ex: João Silva"
            />
            {form.formState.errors.name && (
              <span className="text-red-400 text-xs">
                {form.formState.errors.name.message}
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label className="text-slate-300">Telefone</Label>
              <Input
                {...form.register("phone")}
                onChange={(e) => {
                  const masked = formatPhone(e.target.value);
                  e.target.value = masked;
                  form.setValue("phone", masked);
                }}
                className="bg-slate-800 border-slate-700"
                placeholder="(11) 99999-8888"
                maxLength={15}
              />
              {form.formState.errors.phone && (
                <span className="text-red-400 text-xs">
                  {form.formState.errors.phone.message}
                </span>
              )}
            </div>
            {selectedRole === "COLABORADOR" && (
              <div className="grid gap-2">
                <Label className="text-slate-300">Comissão (%) *</Label>
                <Input
                  type="number"
                  {...form.register("commission_rate", { valueAsNumber: true })}
                  className="bg-slate-800 border-slate-700 font-bold text-emerald-400"
                />
                {form.formState.errors.commission_rate && (
                  <span className="text-red-400 text-xs">
                    {form.formState.errors.commission_rate.message}
                  </span>
                )}
              </div>
            )}
          </div>

          {selectedRole === "COLABORADOR" && (
            <div className="grid gap-2">
              <Label className="text-slate-300">Especialidade / Função</Label>
              <select
                {...form.register("specialty")}
                className="bg-slate-800 border border-slate-700 text-slate-100 h-10 px-3 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="Lavagem e Acabamento">
                  Lavagem e Acabamento
                </option>
                <option value="Polimento e Estética">
                  Polimento e Estética
                </option>
                <option value="Higienização Interna">
                  Higienização Interna
                </option>
                <option value="Gerente de Pátio">Gerente de Pátio</option>
                <option value="Geral">Geral</option>
              </select>
              {form.formState.errors.specialty && (
                <span className="text-red-400 text-xs">
                  {form.formState.errors.specialty.message}
                </span>
              )}
            </div>
          )}

          <div className="grid gap-2">
            <Label className="text-slate-300">Cargo no Sistema</Label>
            <select
              {...form.register("system_role")}
              className="bg-slate-800 border border-slate-700 text-slate-100 h-10 px-3 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="COLABORADOR">
                Colaborador (Acesso restrito ao Kanban)
              </option>
              <option value="ATENDENTE">
                Atendente (Cria ordens e clientes)
              </option>
              <option value="CAIXA">
                Caixa (Pode fechar ordens e ver fluxo de caixa)
              </option>
              <option value="ADMIN">Administrador (Total Acesso)</option>
            </select>
            {form.formState.errors.system_role && (
              <span className="text-red-400 text-xs">
                {form.formState.errors.system_role.message}
              </span>
            )}
          </div>

          <div className="pt-4 flex justify-end gap-2 border-t border-slate-800">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setIsOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isPending}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isPending
                ? "Salvando..."
                : editingCollab
                  ? "Salvar Alterações"
                  : "Cadastrar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
